import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faList,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customerApi } from 'api/endpoints/Customer';
import { getTenantFromToken } from 'utils/routeUtils';
import { formSubmissionToast } from 'helpers/utils';
import {
  getCountryData,
  getStateData,
  getDistrictData,
  getSubCountyData,
  getParishData
} from 'api/endpoints/Location';
import MapModal from 'screens/common/dynamicForm/dynamicNormalForm/FormFields/Mapmodel';
import { fetchAddressFromOla } from 'screens/common/dynamicForm/dynamicNormalForm/FormFields/Mapmodel/services/fetchAddressfromOla';

function CreateCustomer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();

  // Form states
  const [customerName, setCustomerName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [country, setCountry] = useState('');
  const [district, setDistrict] = useState('');
  const [subCounty, setSubCounty] = useState('');
  const [parish, setParish] = useState('');
  const [village, setVillage] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [pinLocation, setPinLocation] = useState('');

  // Dropdown ID states
  const [countryId, setCountryId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [subCountyId, setSubCountyId] = useState('');
  const [parishId, setParishId] = useState('');
  const [villageId, setVillageId] = useState('');

  // Dropdown Option lists
  const [countries, setCountries] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subCounties, setSubCounties] = useState([]);
  const [parishes, setParishes] = useState([]);
  const [villages, setVillages] = useState([]);

  // Map Modal states
  const [showMapModal, setShowMapModal] = useState(false);
  const [pinAddress, setPinAddress] = useState('');

  // UI/API states
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load countries
  const loadCountries = async () => {
    try {
      const response = await getCountryData();
      setCountries(response.data?.results || []);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const handleCountryChange = async cid => {
    setCountryId(cid);
    setDistrictId('');
    setSubCountyId('');
    setParishId('');
    setVillageId('');
    setDistricts([]);
    setSubCounties([]);
    setParishes([]);
    setVillages([]);

    const selectedLabel =
      countries.find(c => c.value === Number(cid))?.label || '';
    setCountry(selectedLabel);
    setIsDirty(true);

    if (cid) {
      try {
        const res = await getStateData({ countryId: cid });
        setDistricts(res.data?.results || []);
      } catch (error) {
        console.error('Error fetching districts:', error);
      }
    }
  };

  const handleDistrictChange = async did => {
    setDistrictId(did);
    setSubCountyId('');
    setParishId('');
    setVillageId('');
    setSubCounties([]);
    setParishes([]);
    setVillages([]);

    const selectedLabel =
      districts.find(d => d.value === Number(did))?.label || '';
    setDistrict(selectedLabel);
    setIsDirty(true);

    if (did) {
      try {
        const res = await getDistrictData({ stateId: did });
        setSubCounties(res.data?.results || []);
      } catch (error) {
        console.error('Error fetching subcounties:', error);
      }
    }
  };

  const handleSubCountyChange = async scid => {
    setSubCountyId(scid);
    setParishId('');
    setVillageId('');
    setParishes([]);
    setVillages([]);

    const selectedLabel =
      subCounties.find(s => s.value === Number(scid))?.label || '';
    setSubCounty(selectedLabel);
    setIsDirty(true);

    if (scid) {
      try {
        const res = await getSubCountyData({ districtId: scid });
        setParishes(res.data?.results || []);
      } catch (error) {
        console.error('Error fetching parishes:', error);
      }
    }
  };

  const handleParishChange = async pid => {
    setParishId(pid);
    setVillageId('');
    setVillages([]);

    const selectedLabel =
      parishes.find(p => p.value === Number(pid))?.label || '';
    setParish(selectedLabel);
    setIsDirty(true);

    if (pid) {
      try {
        const res = await getParishData({ subCountyId: pid });
        setVillages(res.data?.results || []);
      } catch (error) {
        console.error('Error fetching villages:', error);
      }
    }
  };

  const handleVillageChange = vid => {
    setVillageId(vid);
    const selectedLabel =
      villages.find(v => v.value === Number(vid))?.label || '';
    setVillage(selectedLabel);
    setIsDirty(true);
  };

  const handleLocationSelect = result => {
    if (result && result.data) {
      const { lat, lng, address } = result.data;
      setPinLocation(`${lat},${lng}`);
      setPinAddress(address || '');
      setIsDirty(true);
    }
  };

  const handleNationalIdInput = val => {
    const upperVal = val.toUpperCase();
    setNationalId(upperVal);
    setIsDirty(true);

    if (upperVal.length >= 2) {
      const secondChar = upperVal.charAt(1);
      if (secondChar === 'M') {
        setGender('Male');
      } else if (secondChar === 'F') {
        setGender('Female');
      }
    }
  };

  const getMapDefaultValue = () => {
    if (pinLocation && pinLocation.includes(',')) {
      const [latStr, lngStr] = pinLocation.split(',');
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return null;
  };

  const initializeLocations = async cust => {
    try {
      const countriesRes = await getCountryData();
      const countriesList = countriesRes.data?.results || [];
      setCountries(countriesList);

      const dbCountry = cust.country;
      const foundCountry = countriesList.find(
        c => c.label?.toLowerCase() === dbCountry?.toLowerCase()
      );
      if (!foundCountry) return;

      const cId = foundCountry.value;
      setCountryId(cId);

      const districtsRes = await getStateData({ countryId: cId });
      const districtsList = districtsRes.data?.results || [];
      setDistricts(districtsList);

      const dbDistrict = cust.district;
      const foundDistrict = districtsList.find(
        d => d.label?.toLowerCase() === dbDistrict?.toLowerCase()
      );
      if (!foundDistrict) return;

      const dId = foundDistrict.value;
      setDistrictId(dId);

      const subCountiesRes = await getDistrictData({ stateId: dId });
      const subCountiesList = subCountiesRes.data?.results || [];
      setSubCounties(subCountiesList);

      const dbSubCounty = cust.sub_county;
      const foundSubCounty = subCountiesList.find(
        s => s.label?.toLowerCase() === dbSubCounty?.toLowerCase()
      );
      if (!foundSubCounty) return;

      const sId = foundSubCounty.value;
      setSubCountyId(sId);

      const parishesRes = await getSubCountyData({ districtId: sId });
      const parishesList = parishesRes.data?.results || [];
      setParishes(parishesList);

      const dbParish = cust.parish;
      const foundParish = parishesList.find(
        p => p.label?.toLowerCase() === dbParish?.toLowerCase()
      );
      if (!foundParish) return;

      const pId = foundParish.value;
      setParishId(pId);

      const villagesRes = await getParishData({ subCountyId: pId });
      const villagesList = villagesRes.data?.results || [];
      setVillages(villagesList);

      const dbVillage = cust.village;
      const foundVillage = villagesList.find(
        v => v.label?.toLowerCase() === dbVillage?.toLowerCase()
      );
      if (!foundVillage) return;

      const vId = foundVillage.value;
      setVillageId(vId);
    } catch (e) {
      console.error('Error initializing cascading locations:', e);
    }
  };

  // Fetch existing customer details in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchCustomerDetails = async () => {
        try {
          const response = await customerApi.getCustomer(id);
          if (response.data?.success) {
            const cust = response.data.results || {};
            setCustomerName(cust.customer_name || '');
            setContactPerson(cust.contact_person || '');
            setCountry(cust.country || '');
            setDistrict(cust.district || '');
            setSubCounty(cust.sub_county || '');
            setParish(cust.parish || '');
            setVillage(cust.village || '');
            setNationalId(cust.national_id || '');
            setGender(cust.gender || '');
            setDob(cust.dob || '');
            setPinLocation(cust.pin_location || '');
            if (cust.pin_location && cust.pin_location.includes(',')) {
              const [latStr, lngStr] = cust.pin_location.split(',');
              const lat = parseFloat(latStr);
              const lng = parseFloat(lngStr);
              if (!isNaN(lat) && !isNaN(lng)) {
                const apiKey = process.env.REACT_APP_OLA_API_KEY;
                fetchAddressFromOla(lat, lng, apiKey)
                  .then(res => {
                    setPinAddress(res.address || '');
                  })
                  .catch(err => {
                    console.error('Error fetching address:', err);
                  });
              }
            }

            await initializeLocations(cust);
          }
        } catch (error) {
          console.error('Error fetching customer details:', error);
          toast.error('Failed to load customer details');
        }
      };
      fetchCustomerDetails();
    } else {
      loadCountries();
    }
  }, [id, isEditMode]);

  const handleFieldChange = (setter, value) => {
    setter(value);
    setIsDirty(true);
  };

  const handleClear = () => {
    setCustomerName('');
    setContactPerson('');
    setCountry('');
    setDistrict('');
    setSubCounty('');
    setParish('');
    setVillage('');
    setNationalId('');
    setGender('');
    setDob('');
    setPinLocation('');
    setPinAddress('');

    setCountryId('');
    setDistrictId('');
    setSubCountyId('');
    setParishId('');
    setVillageId('');
    setDistricts([]);
    setSubCounties([]);
    setParishes([]);
    setVillages([]);

    setIsDirty(true);
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();

    if (!customerName.trim()) {
      toast.error('Customer Name is required');
      return;
    }
    if (!contactPerson.trim()) {
      toast.error('Contact Person is required');
      return;
    }
    if (!country) {
      toast.error('Country is required');
      return;
    }
    if (!district.trim()) {
      toast.error('District is required');
      return;
    }
    if (!subCounty.trim()) {
      toast.error('Sub County is required');
      return;
    }
    if (!parish.trim()) {
      toast.error('Parish is required');
      return;
    }
    if (!village.trim()) {
      toast.error('Village is required');
      return;
    }
    if (!nationalId.trim()) {
      toast.error('National ID is required');
      return;
    }
    if (!gender) {
      toast.error('Gender is required');
      return;
    }
    if (!dob) {
      toast.error('Date of Birth is required');
      return;
    }
    if (!pinLocation.trim()) {
      toast.error('Pin Location is required');
      return;
    }

    const payload = {
      customer_name: customerName.trim(),
      contact_person: contactPerson.trim(),
      country: country,
      district: district.trim(),
      sub_county: subCounty.trim(),
      parish: parish.trim(),
      village: village.trim(),
      national_id: nationalId.trim(),
      gender: gender,
      dob: dob,
      pin_location: pinLocation.trim()
    };

    try {
      setSubmitting(true);
      let response;
      if (isEditMode) {
        response = await customerApi.updateCustomer(id, payload);
      } else {
        response = await customerApi.createCustomer(payload);
      }
      formSubmissionToast(response, toast, 'Customer', isEditMode);
      setIsDirty(false);
      navigate(`/${tenant}/customer-report`);
    } catch (error) {
      console.error('Error saving customer:', error);
      formSubmissionToast(error, toast, 'Customer', isEditMode);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      fluid
      className="px-4 py-3"
      style={{ backgroundColor: '#F8F9FC', minHeight: '100vh' }}
    >
      {/* Top Header Card */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body className="d-flex justify-content-between align-items-center py-2 px-3">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              onClick={() => navigate(`/${tenant}/customer-report`)}
              className="text-secondary p-0 me-3 fs-5"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <h5 className="mb-0 fw-bold" style={{ color: '#0A2540' }}>
              Customer Management
            </h5>
          </div>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center px-3 fw-semi-bold btn-sm me-2"
              style={{ borderRadius: '6px', borderWidth: '1.5px' }}
              onClick={() => navigate(`/${tenant}/customer-report`)}
            >
              <FontAwesomeIcon icon={faList} className="me-2" />
              View All Customers
            </Button>
            <Button variant="link" className="text-secondary p-0 fs-5">
              <FontAwesomeIcon icon={faEllipsisV} />
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Main Title Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2
          className="mb-0 fw-bold"
          style={{ color: '#0A2540', fontSize: '24px' }}
        >
          {isEditMode ? 'Edit Customer' : 'Customer Creation Screen'}
        </h2>
        <div
          style={{ color: '#6C7A9C', fontSize: '14px' }}
          className="fw-semi-bold"
        >
          Customer &gt;{' '}
          <span className="text-primary">{isEditMode ? 'Edit' : 'New'}</span>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4 mb-5 justify-content-center">
          <Col lg={10} md={12}>
            <Card
              className="shadow-sm border-0"
              style={{ borderRadius: '12px' }}
            >
              <Card.Body className="p-4">
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Customer Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter business or full name"
                        value={customerName}
                        onChange={e =>
                          handleFieldChange(setCustomerName, e.target.value)
                        }
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Contact Person <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter name of contact"
                        value={contactPerson}
                        onChange={e =>
                          handleFieldChange(setContactPerson, e.target.value)
                        }
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Country <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={countryId}
                        onChange={e => handleCountryChange(e.target.value)}
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="">Select Country</option>
                        {countries.map(c => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        District <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={districtId}
                        onChange={e => handleDistrictChange(e.target.value)}
                        disabled={!countryId}
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="">Select District</option>
                        {districts.map(d => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Sub County <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={subCountyId}
                        onChange={e => handleSubCountyChange(e.target.value)}
                        disabled={!districtId}
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="">Select Sub County</option>
                        {subCounties.map(s => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Parish <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={parishId}
                        onChange={e => handleParishChange(e.target.value)}
                        disabled={!subCountyId}
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="">Select Parish</option>
                        {parishes.map(p => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Village <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={villageId}
                        onChange={e => handleVillageChange(e.target.value)}
                        disabled={!parishId}
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="">Select Village</option>
                        {villages.map(v => (
                          <option key={v.value} value={v.value}>
                            {v.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        National ID <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter National ID"
                        value={nationalId}
                        onChange={e => handleNationalIdInput(e.target.value)}
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Gender <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={gender}
                        onChange={e =>
                          handleFieldChange(setGender, e.target.value)
                        }
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Date of Birth (DOB){' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={dob}
                        onChange={e =>
                          handleFieldChange(setDob, e.target.value)
                        }
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Pin Location <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="Latitude, Longitude"
                          value={pinLocation}
                          onChange={e =>
                            handleFieldChange(setPinLocation, e.target.value)
                          }
                          required
                          style={{
                            borderTopLeftRadius: '8px',
                            borderBottomLeftRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        />
                        <Button
                          variant="outline-primary"
                          onClick={() => setShowMapModal(true)}
                          style={{
                            borderTopRightRadius: '8px',
                            borderBottomRightRadius: '8px',
                            border: '1px solid #D8E0F0',
                            borderLeft: 'none'
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-geo-alt-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" />
                          </svg>
                        </Button>
                      </InputGroup>
                      {pinAddress && (
                        <div className="small text-muted mt-2">
                          <strong>Address:</strong> {pinAddress}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Footer Bar */}
        <div
          className="fixed-bottom d-flex justify-content-between align-items-center px-4 py-3 bg-white border-top shadow-lg"
          style={{ zIndex: 1020 }}
        >
          <div>
            {isDirty && (
              <span className="small text-danger fw-bold">
                Unsaved changes in &quot;
                {isEditMode ? 'Edit Customer' : 'New Customer'}&quot;
              </span>
            )}
          </div>
          <div>
            <Button
              variant="outline-secondary"
              className="me-3 px-4 fw-semi-bold btn-sm"
              style={{ borderRadius: '6px' }}
              onClick={handleClear}
            >
              Clear Form
            </Button>
            <Button
              variant="outline-secondary"
              className="me-3 px-4 fw-semi-bold btn-sm"
              style={{ borderRadius: '6px' }}
              onClick={() => navigate(`/${tenant}/customer-report`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              className="px-4 fw-semi-bold btn-sm"
              style={{
                borderRadius: '6px',
                backgroundColor: '#0048A2',
                borderColor: '#0048A2'
              }}
            >
              {submitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Saving...'
                : isEditMode
                ? 'Update Customer'
                : 'Save Customer'}
            </Button>
          </div>
        </div>
      </Form>
      <MapModal
        showModal={showMapModal}
        setShowModal={setShowMapModal}
        mode="location"
        onLocationSelect={handleLocationSelect}
        defaultValue={getMapDefaultValue()}
      />
    </Container>
  );
}

export default CreateCustomer;
